import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { validateInvite, type InviteValidationResponse } from '../api/fetchInvite';

/**
 * Invite Landing Page
 *
 * A verified App Link (Android) or Universal Link (iOS) opens the invite in the
 * app directly, so this page is the landing for everyone else: someone without
 * the app yet, an app older than 1.1.2, or an in-app browser that will not hand
 * the URL over. It validates the token and shows who is inviting them.
 *
 * It used to forward the token onward as familywaffleapp://invite/{token}. Any
 * installed app can register that scheme, and resolution between competing
 * claimants is undefined on both platforms, so a hostile app could claim the
 * invite. It no longer does.
 */

const APP_STORE_URL = 'https://apps.apple.com/app/family-waffle/id6738030746';

// Heroicons link icon
const LinkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
  </svg>
);

// Heroicons exclamation triangle icon
const ExclamationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
  </svg>
);

// Loading spinner
const LoadingSpinner = () => (
  <div className="w-8 h-8 border-2 border-waffle-orange border-t-transparent rounded-full animate-spin" />
);

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const [inviteData, setInviteData] = useState<InviteValidationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation('auth');

  useEffect(() => {
    async function validate() {
      if (!token) {
        setInviteData({ valid: false, error: 'No invite token provided' });
        setLoading(false);
        return;
      }

      try {
        const data = await validateInvite(token);
        setInviteData(data);
      } catch {
        setInviteData({ valid: false, error: 'Failed to validate invite' });
      } finally {
        setLoading(false);
      }
    }

    validate();
  }, [token]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Animated background gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-waffle-orange/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-amber-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative text-center max-w-md w-full">
        <div className="rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 shadow-2xl">
          {/* Logo */}
          <img
            src="/logo.svg"
            alt="Family Waffle"
            className="w-20 h-20 mx-auto mb-6"
          />

          {loading ? (
            // Loading state
            <>
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-waffle-orange to-amber-500 flex items-center justify-center text-white mx-auto mb-4">
                <LoadingSpinner />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">
                {t('invite.validating')}
              </h1>
              <p className="text-sm sm:text-base text-gray-300">
                {t('invite.validatingDesc')}
              </p>
            </>
          ) : inviteData?.valid ? (
            // Valid invite state
            <>
              {/* Sender avatar or default icon */}
              {inviteData.senderPictureUrl ? (
                <img
                  src={inviteData.senderPictureUrl}
                  alt={inviteData.senderName || 'Sender'}
                  className="w-16 h-16 rounded-full mx-auto mb-4 object-cover border-2 border-white/20"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-waffle-orange to-amber-500 flex items-center justify-center text-white mx-auto mb-4">
                  <LinkIcon />
                </div>
              )}

              {/* Title */}
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">
                {t('invite.youreInvited')}
              </h1>

              {/* Description */}
              <p className="text-sm sm:text-base text-gray-300 mb-6">
                {inviteData.senderName
                  ? t('invite.wantsToConnect', { name: inviteData.senderName })
                  : t('invite.someoneWants')
                }
              </p>

              <p className="text-sm text-gray-400 mb-4">
                {t('invite.needsUpdate')}
              </p>

              {/* App Store Link */}
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-gray-400 hover:text-waffle-orange transition-colors text-sm"
              >
                {t('invite.noApp')}
              </a>
            </>
          ) : (
            // Invalid/expired invite state
            <>
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center text-white mx-auto mb-4">
                <ExclamationIcon />
              </div>

              <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">
                {t('invite.invalidInvite')}
              </h1>

              <p className="text-sm sm:text-base text-gray-300 mb-6">
                {inviteData?.error || t('invite.defaultError')}
              </p>

              {/* App Store Link */}
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all"
              >
                {t('invite.getApp')}
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
